import { Form, Formik } from 'formik'
import md5 from 'md5'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useState } from 'react'
import styled from 'styled-components'
import * as Yup from 'yup'

import ErrorBanner from 'errors/components/ErrorBanner'
import { serverSideConfigs, t } from 'localization'
import { emailValidation } from 'shared/addressValidations'
import { Button } from 'shared/components/Button'
import FormikInput from 'shared/components/Input/FormikInput'
import Spinner from 'shared/components/Spinner'
import { AccountQA } from 'shared/dataAttributes'
import { colors, fonts } from 'shared/lib'
import { FCGetServerSidePropsContext, getServerPageProps } from 'shared/utils/getServerSideProps'

const TP = 'pages.orderSearch'

interface IAccountOrderSearchProps {
  error?: string
}
interface IAccountOrderSearchValues {
  orderNumber: string
  email: string
}

const OrderSearch = ({ error }: IAccountOrderSearchProps) => {
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const { locale } = useRouter()
  const orderSearchInputSchema = Yup.object({
    orderNumber: Yup.number().required(
      t(`${TP}.orderNumberVaildation`, 'You must enter an order number'),
    ),
    email: emailValidation(),
  })
  const handleSubmit = ({ orderNumber, email }: IAccountOrderSearchValues) => {
    setLoading(true)
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem('guest-email', email)
    }

    router.push(
      `/order-status/${orderNumber}?q=${md5(email.toLowerCase())}`,
      `/order-status/${orderNumber}?q=${md5(email.toLowerCase())}`,
      { locale },
    )
  }

  return (
    <PageBody>
      <Head>
        <title>{t(`${TP}.pageTitle`, 'Order Search | Flight Club')}</title>
      </Head>
      {error && (
        <ErrorContainer>
          <ErrorBanner data-testid="order-search-error" message={error} />
        </ErrorContainer>
      )}
      <FormContainer data-testid="order-search-form">
        <Spinner
          showSpinner={loading}
          text={t(`${TP}.orderSearchFormSpinner`, 'Searching orders...')}
        >
          <Formik
            initialValues={{
              orderNumber: '',
              email: '',
            }}
            validationSchema={orderSearchInputSchema}
            onSubmit={handleSubmit}
          >
            {({ dirty, isValid }) => (
              <Form>
                <Subtitle data-qa={AccountQA.ManageYourOrdersTitle}>
                  {t(`${TP}.formSubTitle`, 'Manage Your Orders')}
                </Subtitle>
                <BodyText data-qa={AccountQA.ManageYourOrdersDescription}>
                  {t(
                    `${TP}.bodyText`,
                    'Enter your order number and email address used to place your order. You can find the order number in the confirmation email sent to you.',
                  )}
                </BodyText>
                <NumberInput
                  ariaLabel={t(`${TP}.orderNumber`, 'Order Number')}
                  name="orderNumber"
                  qaAttr={AccountQA.OrderSearchOrderNumberBox}
                  title={t(`${TP}.orderNumberTitle`, 'Order #')}
                  type="number"
                />
                <StyledInput
                  title={t(`${TP}.email`, 'Email')}
                  name="email"
                  type="email"
                  qaAttr={AccountQA.OrderSearchEmailBox}
                  autoComplete="email"
                  ariaLabel={t(`${TP}.email`, 'Email')}
                />
                <Button
                  qaAttr={AccountQA.FindOrderButton}
                  $fill
                  buttonType="primary2"
                  disabled={!(isValid && dirty)}
                  type="submit"
                >
                  {t(`${TP}.findOrder`, 'Find Order')}
                </Button>
              </Form>
            )}
          </Formik>
        </Spinner>
        <Divider />
        <Subtitle data-qa={AccountQA.LoginToViewOrderHistoryTitle}>
          {t(`${TP}.loginToViewOrderHistory`, 'Login to View Order History')}
        </Subtitle>
        <BodyText data-qa={AccountQA.LoginToViewOrderHistoryDescription}>
          {t(
            `${TP}.loginToFCAccount`,
            'Login to your Flight Club account to easily track and manage all of your orders.',
          )}
        </BodyText>
        <Button
          data-testid="order-search-login-button"
          qaAttr={AccountQA.LoginButton}
          $fill
          buttonType="primary2"
          href={`/customer/account/login?redirectToMyAccount=true`}
        >
          {t(`${TP}.login`, 'Login')}
        </Button>
      </FormContainer>
    </PageBody>
  )
}
export const getServerSideProps = async (context: FCGetServerSidePropsContext) => {
  const { res, locale } = context
  return {
    props: {
      ...(await getServerPageProps(context)),
      ...(await serverSideConfigs(res, locale)),
    },
  }
}

const PageBody = styled.div`
  padding: 40px 0;
`
const ErrorContainer = styled.div`
  max-width: 580px;
  margin: 0 auto 30px;
`
const FormContainer = styled.div`
  position: relative;
  max-width: 580px;
  margin: 0 auto;
  padding: 30px;
  background-color: white;
`
const StyledInput = styled(FormikInput)`
  margin-bottom: 20px;

  label {
    font-weight: bolder;
    margin-bottom: 11px;
  }
`
/* stylelint-disable */
const NumberInput = styled(StyledInput)`
  input {
    appearance: textfield;
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }
`
/* stylelint-enable */
const Divider = styled.hr`
  margin: 30px 0;
  border: 1px solid ${colors.FC2_LIGHTEST_GREY};
`
const Subtitle = styled.h5`
  ${fonts.SUBTITLE_2}
  margin: 0;
`
const BodyText = styled.p`
  ${fonts.BODY_TEXT}
  margin: 11px 0 30px;
`

export default OrderSearch
